import React, { useMemo } from "react";
import {
    ContractSignatureStatus,
    ContractState,
    Mission,
    ProfileType,
    TerminatedByType,
    TerminateReason,
    User,
} from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import Callout from "../../ui_atoms/Callout";
import Label from "../../ui_atoms/Label";
import { useIntercom } from "../../hooks/intercom";
import { useContractSigningInfo } from "../../hooks/contacts";
import { SwitchComponent, CaseComponent } from "../SwitchCaseComponent";
import Text from "../Text";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet } from "react-native";

type MissionDetailsStatusProps = {
    mission: Mission;
    profileType: ProfileType;
    user?: User | null;
};

const ReasonText = ({ text }: { text?: string | null }) => {
    const i18n = useI18n();
    if (!text) {
        return null;
    }
    return (
        <VStack top={0.5}>
            <Text>{i18n.t("screens.termination.reason.other_description")}</Text>
            <Text>{text}</Text>
        </VStack>
    );
};

const MissionDetailsStatus: React.FC<MissionDetailsStatusProps> = ({
    mission,
    profileType,
    user,
}) => {
    const i18n = useI18n();
    const { presentIntercom } = useIntercom();
    const { getStatusText } = useContractSigningInfo();
    const contract = useMemo(() => mission.currentContract, [mission.currentContract]);
    const signingStatus = contract?.signatureStatus;
    const contractState = contract?.state;

    const styles = useStyles();

    const isSelfTermination = useMemo(() => {
        if (!user?.id || !contract?.terminationOwner?.id) {
            return false;
        }
        return contract?.terminationOwner?.id === user?.id;
    }, [contract?.terminationOwner?.id, user?.id]);

    const terminationOwnerName = useMemo(() => {
        if (contract?.terminationOwner) {
            return `${contract?.terminationOwner?.firstName} ${contract?.terminationOwner?.lastName}`;
        }
        return "";
    }, [contract?.terminationOwner]);

    const signingFailed = useMemo(() => {
        return signingStatus === ContractSignatureStatus.Failed;
    }, [signingStatus]);

    const didntSignInTimeMessage = useMemo(() => {
        if (!contract) {
            return "";
        }
        if (contract.clientContractSignedAt === null) {
            return i18n.t("screens.termination.reason.client_didnt_signed_in_time");
        }
        if (contract.workerContractSignedAt === null) {
            return i18n.t("screens.termination.reason.worker_didnt_signed_in_time");
        }
        if (contract.workerContractSealedAt === null || contract.clientContractSealedAt === null) {
            return i18n.t("screens.termination.reason.lse_didnt_signed_in_time");
        }
        return "";
    }, [contract, i18n]);
    return (
        <SwitchComponent condition={contractState}>
            <CaseComponent value={ContractState.Draft}>
                <Label
                    text={i18n.t("mission.details.draft_title")}
                    mode="default"
                    style={styles.label}
                />
            </CaseComponent>

            <CaseComponent value={ContractState.PendingAcceptation}>
                {signingFailed ? (
                    <Callout
                        mode="error"
                        markdown={i18n.t("screens.user.contract_details.signing_failed")}
                        onLinkPress={() => {
                            void presentIntercom();
                            return true;
                        }}
                    />
                ) : (
                    <Label
                        mode="warning"
                        text={getStatusText(profileType, signingStatus)}
                        style={styles.label}
                    />
                )}
            </CaseComponent>


            <CaseComponent value={ContractState.Cancelled}>
                <SwitchComponent condition={contract?.terminatedBy}>
                    <CaseComponent value={TerminatedByType.Client}>
                        <Label
                            mode="error"
                            text={
                                isSelfTermination
                                    ? i18n.t("screens.termination.header_label.cancelled_by_self")
                                    : i18n.t(
                                          "screens.termination.header_label.cancelled_by_person",
                                          {
                                              personName: terminationOwnerName,
                                          },
                                      )
                            }
                            style={styles.label}
                        />
                        <SwitchComponent condition={contract?.terminationReason}>
                            <CaseComponent value={TerminateReason.NotSignedInTime}>
                                <ReasonText
                                    text={i18n.t(
                                        "screens.termination.reason.client_didnt_signed_in_time",
                                    )}
                                />
                            </CaseComponent>
                            <CaseComponent value={TerminateReason.Other}>
                                <ReasonText text={contract?.terminationOtherReason} />
                            </CaseComponent>
                        </SwitchComponent>
                    </CaseComponent>

                    <CaseComponent value={TerminatedByType.Worker}>
                        <Label
                            mode="error"
                            text={
                                isSelfTermination
                                    ? i18n.t("screens.termination.header_label.cancelled_by_self")
                                    : i18n.t(
                                          "screens.termination.header_label.cancelled_by_person",
                                          {
                                              personName: terminationOwnerName,
                                          },
                                      )
                            }
                            style={styles.label}
                        />
                        <SwitchComponent condition={contract?.terminationReason}>
                            <CaseComponent value={TerminateReason.NotSignedInTime}>
                                <ReasonText
                                    text={i18n.t(
                                        "screens.termination.reason.worker_didnt_signed_in_time",
                                    )}
                                />
                            </CaseComponent>
                            <CaseComponent value={TerminateReason.Other}>
                                <ReasonText text={contract?.terminationOtherReason} />
                            </CaseComponent>
                        </SwitchComponent>
                    </CaseComponent>
                    <CaseComponent value={TerminatedByType.System}>
                        <Label
                            mode="error"
                            text={i18n.t("screens.termination.header_label.cancelled_by_lse")}
                            style={styles.label}
                        />
                        <SwitchComponent condition={contract?.terminationReason}>
                            <CaseComponent value={TerminateReason.NotSignedInTime}>
                                <ReasonText text={didntSignInTimeMessage} />
                            </CaseComponent>
                            <CaseComponent value={TerminateReason.Other}>
                                <ReasonText text={contract?.terminationOtherReason} />
                            </CaseComponent>
                        </SwitchComponent>
                    </CaseComponent>
                </SwitchComponent>
            </CaseComponent>

            <CaseComponent value={ContractState.Terminated}>
                {/* Only terminatedBy == "system" */}
                <Label
                    mode="error"
                    text={i18n.t("screens.user.contract_details.contract_terminated")}
                    style={styles.label}
                />
                {contract?.terminationOtherReason && (
                    <CaseComponent value={TerminateReason.Other}>
                        <ReasonText text={contract.terminationOtherReason} />
                    </CaseComponent>
                )}
            </CaseComponent>

            <CaseComponent value={ContractState.Ended}>
                <Label
                    mode="info"
                    text={i18n.t("mission.details.ended_title")}
                    style={styles.label}
                />
            </CaseComponent>
        </SwitchComponent>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                label: {
                    textAlign: "center",
                    paddingVertical: 2,
                    paddingHorizontal: gridSize / 2,
                },
            }),
        [gridSize],
    );
};

export default MissionDetailsStatus;
